/**
 * Communications Client Helpers
 *
 * Thin `directApiClient`-backed wrappers for the Communications endpoints.
 * Used by both React Query hooks (queries + mutations) and any non-hook
 * consumer that needs to invoke the same operations.
 */

import { directApiClient, DirectApiError } from '@/lib/api/direct-client';
import { getCachedSession } from '@/lib/api/session-cache';
import { getDataConfig } from '@/lib/config/data-config';
import { uploadFileToUrl } from '@/lib/uploads';
import type { ThreadOwner } from './owner';
import type {
  ConversationListResponse,
  ImportCorrespondenceResult,
  ListConversationsParams,
  MatterInstitutionExportCreateResponse,
  MatterInstitutionExportStatusResponse,
  OutlookCorrespondenceSearchResult,
  OutlookSearchAvailability,
  ParticipantType,
  PromoteAttachmentResponse,
  ReplyDraftResponse,
  ReplyDraftAnswer,
  ReplyOfferDto,
  SendAdHocResult,
  ThreadItemsResponse
} from './types';

/**
 * The composer payload, owner-agnostic.
 *
 * Both owners' write paths accept the same fields — that is deliberate on the
 * backend (`LeadThreadComposerDto` mirrors `CreateCorrespondenceDto`'s names)
 * so the shared composer state serialises unchanged for either owner. What
 * differs is the *shape of the call*, which the two branches below encode:
 * a matter takes two requests (create-or-update, then send) because the matter
 * id in the path addresses the draft; a lead takes one, because a lead owns
 * exactly one thread so the payload alone identifies what to write.
 */
interface AdHocComposerPayload {
  correspondenceId?: number;
  participantType: ParticipantType;
  participantId: number;
  subject: string;
  body: string;
  /**
   * `null` withdraws an opt-in an earlier autosave stored on the draft (the
   * follow-up control unchecked); `undefined` leaves the field alone. A lead
   * send never carries one: the enquiry composer offers no control (VALE-1872).
   */
  followUpDays?: number | null;
  additionalRecipientContactMatterIds?: number[];
  attachments?: number[];
  /** Captured on the row at create time when an AD_HOC draft was composed
   *  from an email template. Immutable post-create — Update DTO ignores it
   *  so re-saves of an existing draft don't accidentally clear or
   *  overwrite the source template id. */
  sourceEmailTemplateId?: number;
  /** Set when composing a reply on an existing thread — links the new
   *  AD_HOC row to the message it replies to so the backend renders the
   *  quoted thread history at send time. Create-only: immutable
   *  post-create, so it's omitted from the update branch below. */
  parentCorrespondenceId?: number;
  /** The AI reply draft this body was inserted from (AICOMMS-8). Create-only,
   *  like `sourceEmailTemplateId`: sent only on the matter create call, where it
   *  marks the draft `inserted` and stamps provenance. A lead never carries one. */
  aiDraftId?: number;
}

/**
 * The lead thread's create-or-update body (`LeadThreadComposerDto`).
 *
 * `body` becomes `mainContent` and the participant fields drop away entirely —
 * the lead *is* the participant, so there is no `(participantType,
 * participantId)` pair to send. `additionalRecipientContactMatterIds` is
 * deliberately never forwarded: fan-out recipients are contacts on a matter,
 * and the backend rejects a non-empty value with an explanatory 400 rather
 * than silently ignoring it.
 */
function leadComposerBody(
  payload: AdHocComposerPayload
): Record<string, unknown> {
  return {
    ...(payload.correspondenceId != null
      ? { correspondenceId: payload.correspondenceId }
      : {}),
    subject: payload.subject,
    mainContent: payload.body,
    ...(payload.followUpDays != null
      ? { followUpDays: payload.followUpDays }
      : {}),
    ...(payload.attachments != null
      ? { attachments: payload.attachments }
      : {}),
    ...(payload.sourceEmailTemplateId != null
      ? { sourceEmailTemplateId: payload.sourceEmailTemplateId }
      : {}),
    ...(payload.parentCorrespondenceId != null
      ? { parentCorrespondenceId: payload.parentCorrespondenceId }
      : {})
  };
}

/**
 * `GET /matters/:matterId/communications/threads`
 *
 * Not paginated — backend returns every thread on the matter in one response.
 */
export async function listConversations(
  matterId: number,
  params?: ListConversationsParams
): Promise<ConversationListResponse> {
  return directApiClient.get<ConversationListResponse>(
    `/matters/${matterId}/communications/threads`,
    { params: params as Record<string, unknown> | undefined }
  );
}

/**
 * Thread items + header for one thread, on either owner.
 *
 * - **Matter** — `GET /matters/:matterId/communications/threads/:participantType/:participantId/items`.
 *   The composite key (`participantType`, `participantId`) is required:
 *   institution and contact IDs come from separate auto-increment columns and
 *   can collide.
 * - **Lead** — `GET /leads/:id/thread`. No participant pair: a lead owns
 *   exactly one thread and *is* its participant.
 *
 * Both return the identical `ThreadItemsResponse` shape — the backend builds
 * the lead thread through the same item mapper — which is what lets one
 * `ThreadView` render either owner.
 */
export async function getThreadItems(
  owner: ThreadOwner,
  participantType: ParticipantType,
  participantId: number
): Promise<ThreadItemsResponse> {
  if (owner.type === 'lead') {
    return directApiClient.get<ThreadItemsResponse>(
      `/leads/${owner.leadId}/thread`
    );
  }
  return directApiClient.get<ThreadItemsResponse>(
    `/matters/${owner.matterId}/communications/threads/${participantType}/${participantId}/items`
  );
}

/**
 * Mark a thread read. Idempotent, firm-wide, empty body, no return value.
 *
 * - **Matter** — `POST /matters/:matterId/communications/threads/:participantType/:participantId/mark-read`,
 *   which writes a `thread_read_marks` row.
 * - **Lead** — `POST /leads/:id/thread/mark-read`, which stamps
 *   `threadLastSeenAt` / `threadLastSeenById` on the lead itself (that table's
 *   `matter_id` sits inside two partial unique indexes, so a lead receipt
 *   there would have meant re-keying an index on a live table).
 */
export async function markThreadRead(
  owner: ThreadOwner,
  participantType: ParticipantType,
  participantId: number
): Promise<void> {
  if (owner.type === 'lead') {
    await directApiClient.post<void>(
      `/leads/${owner.leadId}/thread/mark-read`,
      {}
    );
    return;
  }
  await directApiClient.post<void>(
    `/matters/${owner.matterId}/communications/threads/${participantType}/${participantId}/mark-read`,
    {}
  );
}

/**
 * `POST /matters/:matterId/communications/files/:fileId/promote-to-documents`
 *
 * One-way action. Flips the file's `fileType` from `correspondence-attachment`
 * to `additional-document` and moves its S3 object. After promotion the file
 * shows up in the Documents tab; to remove it from Documents the user goes
 * through the Documents-tab delete flow (no reverse-promote endpoint).
 */
/**
 * Persist an AD_HOC draft, on either owner. Lazy-create: when no
 * `correspondenceId` is supplied a new draft is created; subsequent calls with
 * the returned id update it. Returns the resulting `correspondenceId` so the
 * caller can stash it for the next save. Matches D-16 — no row exists until
 * the user clicks Save Draft, clicks Send, or adds the first attachment.
 *
 * **Matter** — two routes (`POST /matters/:matterId/correspondences`, then
 * `PUT .../:id`), because the matter id in the path addresses the draft.
 * **Lead** — one route (`POST /leads/:id/thread/draft`), create-or-update
 * resolved server-side from the payload; a lead owns exactly one thread, so
 * there is no path id to address a draft with.
 */
export async function saveAdHocDraft(
  owner: ThreadOwner,
  payload: AdHocComposerPayload
): Promise<{ correspondenceId: number }> {
  if (owner.type === 'lead') {
    // One call, create-or-update: the backend resolves "the lead's draft"
    // from `correspondenceId`'s presence, so the FE needs no create/update
    // branch of its own here.
    const saved = await directApiClient.post<{ id: number }>(
      `/leads/${owner.leadId}/thread/draft`,
      leadComposerBody(payload)
    );
    return { correspondenceId: saved.id };
  }
  const matterId = owner.matterId;
  if (payload.correspondenceId) {
    // Update DTO accepts only mutable fields — `type` and the participant FKs
    // are immutable post-create, and class-validator whitelist mode rejects
    // anything else with a 400. `sourceEmailTemplateId` is set at create
    // time only; intentionally omitted from update calls.
    await directApiClient.put<unknown>(
      `/matters/${matterId}/correspondences/${payload.correspondenceId}`,
      {
        subject: payload.subject,
        mainContent: payload.body,
        followUpDays: payload.followUpDays,
        additionalRecipientContactMatterIds:
          payload.additionalRecipientContactMatterIds,
        attachments: payload.attachments
      }
    );
    return { correspondenceId: payload.correspondenceId };
  }
  const created = await directApiClient.post<{ id: number }>(
    `/matters/${matterId}/correspondences`,
    {
      type: 'ad-hoc',
      ...(payload.participantType === 'institution'
        ? { matterInstitutionId: payload.participantId }
        : { contactMatterId: payload.participantId }),
      subject: payload.subject,
      mainContent: payload.body,
      followUpDays: payload.followUpDays,
      additionalRecipientContactMatterIds:
        payload.additionalRecipientContactMatterIds,
      attachments: payload.attachments,
      ...(payload.sourceEmailTemplateId != null
        ? { sourceEmailTemplateId: payload.sourceEmailTemplateId }
        : {}),
      ...(payload.aiDraftId != null ? { aiDraftId: payload.aiDraftId } : {}),
      ...(payload.parentCorrespondenceId != null
        ? { parentCorrespondenceId: payload.parentCorrespondenceId }
        : {})
    }
  );
  return { correspondenceId: created.id };
}

/**
 * Discard the composer's draft, on either owner (LF-6023).
 *
 * **Matter** — `DELETE /matters/:matterId/correspondences/:id`.
 * **Lead** — `DELETE /leads/:id/thread/draft/:correspondenceId`.
 *
 * Both soft-delete and both refuse anything that has left DRAFT, so a message that
 * has actually gone out stays on the thread. This was the one composer affordance a
 * lead lacked outright: its draft could be *replaced* by the next autosave but never
 * explicitly thrown away.
 */
export async function discardAdHocDraft(
  owner: ThreadOwner,
  correspondenceId: number
): Promise<void> {
  if (owner.type === 'lead') {
    await directApiClient.delete<void>(
      `/leads/${owner.leadId}/thread/draft/${correspondenceId}`
    );
    return;
  }
  await directApiClient.delete<void>(
    `/matters/${owner.matterId}/correspondences/${correspondenceId}`
  );
}

/**
 * Retry a send that terminally failed, on either owner (LF-6023).
 *
 * **Matter** — `POST /matters/:matterId/correspondences/:id/send`.
 * **Lead** — `POST /leads/:id/thread/:correspondenceId/retry-send`.
 *
 * The failure banner on a quarantined outbound card renders on both owners — the
 * failure is true either way — but only the matter had a route behind the Retry
 * action. Both now re-enter the same send pipeline, which already treats a failed
 * send as re-sendable and clears the previous attempt's failure marker on success.
 */
export async function retryThreadSend(
  owner: ThreadOwner,
  correspondenceId: number
): Promise<void> {
  if (owner.type === 'lead') {
    await directApiClient.post<unknown>(
      `/leads/${owner.leadId}/thread/${correspondenceId}/retry-send`,
      {}
    );
    return;
  }
  await directApiClient.post<unknown>(
    `/matters/${owner.matterId}/correspondences/${correspondenceId}/send`,
    {}
  );
}

/**
 * Send an ad-hoc email on a Communications thread, on either owner.
 *
 * **Matter** — two-step against the existing Correspondences API (the
 * Communications spec intentionally extends those endpoints rather than
 * introducing new ones — see backend spec §4.3):
 *
 *   1. `POST /matters/:matterId/correspondences` to persist an `AD_HOC` draft
 *      with subject + body (stored as `fragments.mainContent`) + participant
 *      FK.
 *   2. `POST /matters/:matterId/correspondences/:id/send` to flip the draft
 *      to SENT — server appends the firm signature, computes follow-up
 *      due-at, restarts prior outbound timers, fans out siblings.
 *
 * **Lead** — one step (`POST /leads/:id/thread/send`), which persists the
 * composer state and then runs *the same* send pipeline unchanged: SES
 * transport (a lead send is never Outlook — §7.4), firm signature, follow-up
 * timer, quarantine-on-failure. So the `SendAdHocResult` contract, including
 * the `blocked` branch the caller surfaces, is identical for both owners.
 */
export async function sendAdHocEmail(
  owner: ThreadOwner,
  payload: AdHocComposerPayload & {
    excludeFirmSignature?: boolean;
    sendSenderCopy?: boolean;
  }
): Promise<SendAdHocResult> {
  if (owner.type === 'lead') {
    // Save-then-send in a single call, mirroring what the matter path does in
    // two — which also removes the window where a send would dispatch the
    // *previously* autosaved body because the latest save hadn't landed yet.
    return directApiClient.post<SendAdHocResult>(
      `/leads/${owner.leadId}/thread/send`,
      {
        ...leadComposerBody(payload),
        ...(payload.sendSenderCopy ? { sendSenderCopy: true } : {}),
        ...(payload.excludeFirmSignature ? { excludeFirmSignature: true } : {})
      }
    );
  }
  const matterId = owner.matterId;
  let draftId: number;
  if (payload.correspondenceId) {
    // Update the existing draft with latest composer state, then send it.
    // The update DTO whitelists only mutable fields — `type` and participant
    // FKs are immutable post-create and would 400 if included.
    // `sourceEmailTemplateId` is set at create time only; sending re-uses
    // whatever was persisted on the draft row.
    await directApiClient.put<unknown>(
      `/matters/${matterId}/correspondences/${payload.correspondenceId}`,
      {
        subject: payload.subject,
        mainContent: payload.body,
        followUpDays: payload.followUpDays,
        additionalRecipientContactMatterIds:
          payload.additionalRecipientContactMatterIds,
        attachments: payload.attachments
      }
    );
    draftId = payload.correspondenceId;
  } else {
    const created = await directApiClient.post<{ id: number }>(
      `/matters/${matterId}/correspondences`,
      {
        type: 'ad-hoc',
        ...(payload.participantType === 'institution'
          ? { matterInstitutionId: payload.participantId }
          : { contactMatterId: payload.participantId }),
        subject: payload.subject,
        mainContent: payload.body,
        followUpDays: payload.followUpDays,
        additionalRecipientContactMatterIds:
          payload.additionalRecipientContactMatterIds,
        attachments: payload.attachments,
        ...(payload.sourceEmailTemplateId != null
          ? { sourceEmailTemplateId: payload.sourceEmailTemplateId }
          : {}),
        ...(payload.aiDraftId != null ? { aiDraftId: payload.aiDraftId } : {}),
        ...(payload.parentCorrespondenceId != null
          ? { parentCorrespondenceId: payload.parentCorrespondenceId }
          : {})
      }
    );
    draftId = created.id;
  }
  const sendBody: Record<string, unknown> = {};
  if (payload.excludeFirmSignature) sendBody.excludeFirmSignature = true;
  if (payload.sendSenderCopy) sendBody.sendSenderCopy = true;
  // The send response carries the Outlook blocked summary (`status: 'blocked'`)
  // when the firm's sending identity isn't connected — the caller surfaces it
  // instead of a success toast, and the draft stays put (§6.3/§6.4).
  return directApiClient.post<SendAdHocResult>(
    `/matters/${matterId}/correspondences/${draftId}/send`,
    sendBody
  );
}

/**
 * `POST /matters/:matterId/matter-institutions/:matterInstitutionId/exports`
 *
 * Kicks off a Temporal-orchestrated export that bundles every correspondence
 * (outbound + inbound), all attachments, formal-letter PDFs, email-body PDFs,
 * and every file note on the institution thread into a single ZIP. Returns
 * immediately with `status:'processing'`; poll the GET endpoint for the
 * presigned download URL.
 */
export async function createInstitutionExport(
  matterId: number,
  matterInstitutionId: number
): Promise<MatterInstitutionExportCreateResponse> {
  return directApiClient.post<MatterInstitutionExportCreateResponse>(
    `/matters/${matterId}/matter-institutions/${matterInstitutionId}/exports`,
    {}
  );
}

/**
 * `GET /matters/:matterId/matter-institutions/:matterInstitutionId/exports/:exportId`
 *
 * Status poll. On `completed` returns `downloadUrl` (presigned S3, valid 1h —
 * regenerated on next call after expiry), `fileCount`, `totalSizeBytes`. On
 * `failed` returns `errorMessage`.
 */
export async function getInstitutionExport(
  matterId: number,
  matterInstitutionId: number,
  exportId: number
): Promise<MatterInstitutionExportStatusResponse> {
  return directApiClient.get<MatterInstitutionExportStatusResponse>(
    `/matters/${matterId}/matter-institutions/${matterInstitutionId}/exports/${exportId}`
  );
}

/**
 * Import correspondence that never flowed through Vale into a thread (VALE-597).
 *
 * Two steps, deliberately: the bytes go to S3 through the app's one presigned-URL upload
 * path (so this surface inherits real progress, abort and retry rather than
 * re-implementing transport), and this call then tells the backend to make a
 * correspondence out of the file that landed. There is no confirm step in between —
 * direction, transport and dates are all derivable server-side, and everything else the
 * document-analysis pipeline fills in.
 *
 * A duplicate comes back 200 with `outcome: 'duplicate'` and the id of the item already on
 * the thread — not an error, because the user did nothing wrong.
 */
export async function importCorrespondenceIntoThread(
  matterId: number,
  participantType: ParticipantType,
  participantId: number,
  fileId: number
): Promise<ImportCorrespondenceResult> {
  return directApiClient.post<ImportCorrespondenceResult>(
    `/matters/${matterId}/communications/threads/${participantType}/${participantId}/import`,
    { fileId }
  );
}

/**
 * Import a correspondence into a thread straight from the firm's PMS (VALE-1124).
 *
 * **Names the PMS document, not a Vale file** — the counterpart of
 * {@link importCorrespondenceIntoThread}, whose `fileId` only exists because the browser
 * put those bytes on S3 itself. Here the bytes are in Smokeball or ActionStep and the
 * browser never touches them, so the whole thing is one server call: Vale checks the
 * document is on this matter, downloads it, lands it as an import source and parses it.
 *
 * Deliberately not "land it with the shared PMS picker, then call the import route with
 * the id" — that shape leaves an orphaned file on the matter whenever it fails between the
 * two calls, and the import surface promises that cancelling leaves nothing behind.
 *
 * Same result type as the hand-upload import, duplicates included, because downstream of
 * the landed file it IS the hand-upload import.
 */
export async function importPmsCorrespondenceIntoThread(
  matterId: number,
  participantType: ParticipantType,
  participantId: number,
  document: { externalId: string; fileName: string }
): Promise<ImportCorrespondenceResult> {
  return directApiClient.post<ImportCorrespondenceResult>(
    `/matters/${matterId}/communications/threads/${participantType}/${participantId}/import/pms`,
    document
  );
}

/**
 * The path a thread's mailbox-search and import routes hang off, for either owner
 * (VALE-2014). An enquiry owns one thread, so its routes need no participant pair and sit
 * under `/leads/:id/thread`; the four suffixes below are identical on both.
 */
function importSurfaceBase(
  owner: ThreadOwner,
  participantType: ParticipantType,
  participantId: number
): string {
  return owner.type === 'lead'
    ? `/leads/${owner.leadId}/thread`
    : `/matters/${owner.matterId}/communications/threads/${participantType}/${participantId}`;
}

/**
 * Can this viewer search their connected Outlook mailbox from this thread (VALE-1217)?
 *
 * Two questions in one answer, because either alone is misleading: whether the viewer has
 * a mailbox Vale may search, and whether this thread's participant has an address to search
 * for. With no address there is nothing tying a mailbox search to this matter, so the route
 * is not offered rather than offered and then empty.
 */
export async function getOutlookSearchAvailability(
  owner: ThreadOwner,
  participantType: ParticipantType,
  participantId: number
): Promise<OutlookSearchAvailability> {
  return directApiClient.get<OutlookSearchAvailability>(
    `${importSurfaceBase(owner, participantType, participantId)}/outlook/availability`
  );
}

/**
 * One page of messages exchanged with this thread's participant, from the viewer's own
 * connected mailbox (VALE-1217).
 *
 * **The address is not a parameter, and that is the point.** The backend resolves it from
 * the thread's participant, so this cannot be asked to search the mailbox for anything else
 * — which is the only bound there is, since a mailbox has no notion of a matter.
 *
 * `cursor` is the previous page's `nextCursor`, passed back verbatim.
 */
export async function searchOutlookCorrespondence(
  owner: ThreadOwner,
  participantType: ParticipantType,
  participantId: number,
  cursor?: string | null
): Promise<OutlookCorrespondenceSearchResult> {
  return directApiClient.post<OutlookCorrespondenceSearchResult>(
    `${importSurfaceBase(owner, participantType, participantId)}/outlook/search`,
    cursor ? { cursor } : {}
  );
}

/**
 * Import one message picked out of the connected Outlook mailbox (VALE-1217).
 *
 * **One message per call.** A batch is the caller looping this, matching what the PMS
 * multi-attach and the multi-file drop already do — sequential, and a refusal on one does
 * not stop the rest. Same result type as every other import door, duplicates included,
 * because downstream of the fetched bytes it IS the same import.
 */
export async function importOutlookCorrespondenceIntoThread(
  owner: ThreadOwner,
  participantType: ParticipantType,
  participantId: number,
  message: {
    graphMessageId: string;
    internetMessageId?: string | null;
    subject?: string | null;
  }
): Promise<ImportCorrespondenceResult> {
  return directApiClient.post<ImportCorrespondenceResult>(
    `${importSurfaceBase(owner, participantType, participantId)}/import/outlook`,
    {
      graphMessageId: message.graphMessageId,
      ...(message.internetMessageId
        ? { internetMessageId: message.internetMessageId }
        : {}),
      ...(message.subject ? { subject: message.subject } : {})
    }
  );
}

/**
 * Undo one import — the "wrong conversation" action (VALE-597).
 *
 * The backend refuses anything that was not imported, so this can never reach a message
 * Vale captured or sent; the FE therefore only needs to offer it on imported items.
 */
export async function deleteImportedCorrespondence(
  owner: ThreadOwner,
  participantType: ParticipantType,
  participantId: number,
  correspondenceId: number
): Promise<void> {
  await directApiClient.delete<void>(
    `${importSurfaceBase(owner, participantType, participantId)}/import/${correspondenceId}`
  );
}

/**
 * Put an import's bytes on S3 and return the `files` row id the import endpoint consumes.
 *
 * The standard three-step Files upload (`src/files/CLAUDE.md`): presign → PUT → complete.
 * `analysisRequested` is set at presign because every import is analysed — for a scanned
 * letter that flag is what enrols the file in the document-analysis pipeline, and for an
 * email it is inherited by the attachments the backend unwraps.
 */
export async function uploadImportSourceFile(
  matterId: number,
  file: File,
  opts: { onProgress?: (percent: number) => void; signal?: AbortSignal } = {}
): Promise<number> {
  const { presignedUrl, fileId } = await directApiClient.post<{
    presignedUrl: string;
    fileId: number;
  }>('/files/pre-signed-url', {
    fileType: 'imported-correspondence',
    matterId,
    analysisRequested: true
  });

  await uploadFileToUrl(presignedUrl, file, {
    onProgress: (p) => opts.onProgress?.(p.percent),
    signal: opts.signal
  });

  await directApiClient.post<unknown>(`/files/${fileId}/upload-complete`, {
    originalFilename: file.name
  });

  return fileId;
}

export async function promoteAttachmentToDocuments(
  matterId: number,
  fileId: number
): Promise<PromoteAttachmentResponse> {
  return directApiClient.post<PromoteAttachmentResponse>(
    `/matters/${matterId}/communications/files/${fileId}/promote-to-documents`,
    {}
  );
}

// ───────────────────────────────────────────────────────────────────────────
// AI reply offers & drafts (AICOMMS-8)
//
// Matter-only by design: a lead thread carries no offer. Each helper takes a
// plain `matterId` for that reason, matching the other matter-only helpers
// above (promote-to-documents, the exports).
// ───────────────────────────────────────────────────────────────────────────

/**
 * The backend's offer routes 404 for a message that cannot carry an offer.
 * That means "no offer" to the composer, not an error, so a 404 resolves to
 * `null` here rather than throwing. Every other status still throws.
 */
function nullOnNotFound(error: unknown): null {
  if (error instanceof DirectApiError && error.status === 404) return null;
  throw error;
}

/**
 * `POST /matters/:matterId/communications/threads/:participantType/:participantId/reply-offer`
 *
 * The lazy path: computes and stores the offer for the thread's latest inbound
 * message when none is stored, and returns it (201). Idempotent per message.
 * The common case needs no call at all — the offer rides the latest inbound
 * thread item's `replyOffer`.
 */
export async function fetchReplyOffer(
  matterId: number,
  participantType: ParticipantType,
  participantId: number
): Promise<ReplyOfferDto | null> {
  try {
    return await directApiClient.post<ReplyOfferDto>(
      `/matters/${matterId}/communications/threads/${participantType}/${participantId}/reply-offer`,
      {}
    );
  } catch (error) {
    return nullOnNotFound(error);
  }
}

/**
 * `POST /matters/:matterId/communications/ai-drafts/:aiDraftId/discard`
 *
 * Marks the draft `discarded` (the KPI signal for "Clear and write my own").
 * 204, no body. Best-effort from the caller's point of view — the composer has
 * already cleared the body.
 */
export async function discardReplyDraft(
  matterId: number,
  aiDraftId: number
): Promise<void> {
  await directApiClient.post<void>(
    `/matters/${matterId}/communications/ai-drafts/${aiDraftId}/discard`,
    {}
  );
}

export interface DraftReplyArgs {
  matterId: number;
  participantType: ParticipantType;
  participantId: number;
  stanceId: string;
  /** The lawyer's answers to the stance's questions (VALE-1883). */
  answers?: ReplyDraftAnswer[];
  /** Aborts the model call (Cancel, click-away, unmount). */
  signal?: AbortSignal;
}

/**
 * `POST /matters/:matterId/communications/threads/:participantType/:participantId/draft-reply`
 * with `{ stanceId, answers }`, returning the whole draft at once (VALE-1883).
 *
 * The draft used to stream paragraph by paragraph over server-sent events
 * (AICOMMS-7). It now arrives whole, because the model writes the letter and
 * then the facts behind it, so nothing can be checked, and so shown, before the
 * end; the route still streams to a client that asks for `text/event-stream`,
 * and this one asks for JSON.
 *
 * A hand-rolled `fetch` rather than `directApiClient`, because the call must be
 * abortable (Cancel, a thread switch). Auth is assembled exactly as
 * `directApiClient` does (the NextAuth session token plus the tenant header), so
 * the two agree on who is calling.
 */
export async function draftReply({
  matterId,
  participantType,
  participantId,
  stanceId,
  answers,
  signal
}: DraftReplyArgs): Promise<ReplyDraftResponse> {
  const session = await getCachedSession();
  if (!session?.accessToken) {
    throw new DirectApiError('Not authenticated', 401);
  }
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    Authorization: `Bearer ${session.accessToken}`
  };
  if (session.tenantId) headers['X-Tenant-Id'] = session.tenantId;

  const url = `${getDataConfig().apiBaseUrl}/matters/${matterId}/communications/threads/${participantType}/${participantId}/draft-reply`;

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({ stanceId, answers: answers ?? [] }),
    signal
  });

  if (!response.ok) {
    let errorData: { message?: string; code?: string } | undefined;
    try {
      errorData = await response.json();
    } catch {
      errorData = undefined;
    }
    throw new DirectApiError(
      errorData?.message ?? `Draft request failed (${response.status})`,
      response.status,
      errorData
    );
  }

  return (await response.json()) as ReplyDraftResponse;
}
